import Modal from '@/components/common/modal';
import { useTranslations } from '@/i18n/use-translations';
import {
  Paper,
  Typography,
  Stack,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Box,
  Grid,
  Card,
  CardContent,
  Divider,
  CircularProgress,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import { useSubscriptionPlansQuery } from '@/state/queries/transactions/use-subscription-plans.ts';
import { useGetOrganizationQuery } from '@/state/queries/organizations/use-get-organization';
import { useQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import {
  type SubscriptionCheckoutCartLineDto,
  type SubscriptionCheckoutCartPlanChangeDraft,
  normalizeQuotaExtrasByTierKeys,
  quotaExtrasByCardFromPlanChangeDraft,
} from '@/state/queries/transactions/use-subscription-checkout-cart';
import type {
  SubscriptionBillingPeriod,
  SubscriptionPlan,
  SubscriptionTier,
} from '@/types/dtos/transactions/subscription-plan.dto';
import { usePreviewOrganizationPlanChangeMutation } from '@/state/mutations/transactions/use-preview-organization-plan-change-mutation';
import {
  usePatchOrganizationSubscriptionCheckoutCart,
  type PatchOrganizationSubscriptionCheckoutCartBody,
} from '@/state/mutations/transactions/use-patch-organization-subscription-checkout-cart';
import ManagePlanIncludesList from '@/components/organizations/plan/manage-plan-includes-list';
import {
  getManagePlanFeatureListBullets,
  resolveIncludedFeatureKeyDisplayRows,
  resolveIncludedQuotaDisplayRows,
} from '@/helpers/manage-plan-feature-display';
import { useNotifications } from '@/hooks/use-notifications';
import type { ChangeOrganizationSubscriptionPlan } from '@/types/dtos/transactions/request/change-organization-subscription-plan.dto';
import type { OrganizationPlanChangePreview } from '@/types/dtos/transactions/organization-plan-change-preview.dto';
import SubscriptionPlanChangeConfirmModal from '@/components/organizations/plan/subscription-plan-change-confirm.modal';
import {
  computeOrganizationRenewalAmount,
  computePreviewSubscriptionPrice,
  formatSubscriptionInstancePeriodEndDisplay,
  includedQuantityForKey,
  maxQuantityForKey,
  mergeOverageFromMatchingSubscriptionSnapshot,
  normalizeSubscriptionPlanOverage,
} from '@/helpers/organization-subscription-pricing';
import { resolveTranslatedProductField, resolveProductClaimText } from '@/helpers/resolve-translated-product-field';
import { PlanCardQuotaSteppers } from './plan-card-quota-steppers';
import { getPrimaryOrganizationSubscriptionInstance } from '@/helpers/organization-subscriptions';
import {
  defaultMainSubscriptionProductKeyForCategory,
  resolveAddOnParentInstanceId,
  resolveParentBillingPeriodForAddonFromOrgAndCart,
} from '@/helpers/organization-plan-catalog';

const HOST_DEFAULT_PRODUCT_KEY = defaultMainSubscriptionProductKeyForCategory('HOST');

const ORDER: SubscriptionTier[] = ['FREE', 'BASIC', 'SMART', 'PRO', 'PREMIUM'];

function normalizedPositiveQuotaAddonList(
  units: { quotaKey: string; quantity: number }[] | undefined,
): { quotaKey: string; quantity: number }[] {
  const m = new Map<string, number>();
  for (const u of units ?? []) {
    const k = String(u.quotaKey ?? '').trim();
    if (!k || u.quantity <= 0) {
      continue;
    }
    m.set(k, u.quantity);
  }
  return [...m.entries()].map(([quotaKey, quantity]) => ({ quotaKey, quantity }));
}

function quotaAddonListsEqual(
  a: { quotaKey: string; quantity: number }[] | undefined,
  b: { quotaKey: string; quantity: number }[] | undefined,
): boolean {
  const sa = normalizedPositiveQuotaAddonList(a).sort((x, y) => x.quotaKey.localeCompare(y.quotaKey));
  const sb = normalizedPositiveQuotaAddonList(b).sort((x, y) => x.quotaKey.localeCompare(y.quotaKey));
  if (sa.length !== sb.length) {
    return false;
  }
  return sa.every((row, i) => row.quotaKey === sb[i]!.quotaKey && row.quantity === sb[i]!.quantity);
}

/** Stack tier cards until `lg`; side-by-side only when the viewport is wide enough (matches manage/checkout modal comfort). */
function tierCardGridSize(planCount: number): { xs: number; lg?: number } {
  if (planCount <= 1) {
    return { xs: 12 };
  }
  if (planCount === 2) {
    return { xs: 12, lg: 6 };
  }
  return { xs: 12, lg: 4 };
}

function pickPlan(
  plans: SubscriptionPlan[],
  tier: SubscriptionTier,
  billing: SubscriptionBillingPeriod,
): SubscriptionPlan | undefined {
  return (
    plans.find((p) => p.tier === tier && (p.billingPeriod ?? 'MONTH') === billing) ?? plans.find((p) => p.tier === tier)
  );
}

function soleTierFromPlansForProductKey(
  allPlans: SubscriptionPlan[],
  subscriptionProductKey: string,
): SubscriptionTier | undefined {
  const want = subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
  const subset = allPlans.filter((p) => (p.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) === want);
  const tiers = [...new Set(subset.map((p) => p.tier))] as SubscriptionTier[];
  return tiers.length === 1 ? tiers[0] : undefined;
}

function cartLineDtoToPatchBody(
  line: SubscriptionCheckoutCartLineDto,
  allPlans?: SubscriptionPlan[],
): NonNullable<PatchOrganizationSubscriptionCheckoutCartBody['lines']>[number] {
  const inferred =
    !line.tier?.trim() && allPlans?.length
      ? soleTierFromPlansForProductKey(allPlans, line.subscriptionProductKey)
      : undefined;
  const tier = (line.tier?.trim() || inferred) as SubscriptionTier | undefined;
  return {
    subscriptionProductKey: line.subscriptionProductKey,
    ...(tier ? { tier } : {}),
    billingPeriod: line.billingPeriod as SubscriptionBillingPeriod | undefined,
    additionalQuotaUnits: line.additionalQuotaUnits,
    ...(line.parentSubscriptionInstanceId ? { parentSubscriptionInstanceId: line.parentSubscriptionInstanceId } : {}),
  };
}

function buildBillingOptions(plans: SubscriptionPlan[] | undefined): SubscriptionBillingPeriod[] {
  if (!plans?.length) {
    return ['MONTH'];
  }
  const set = new Set<SubscriptionBillingPeriod>();
  for (const p of plans) {
    set.add((p.billingPeriod ?? 'MONTH') as SubscriptionBillingPeriod);
  }
  const ordered: SubscriptionBillingPeriod[] = [];
  if (set.has('MONTH')) {
    ordered.push('MONTH');
  }
  if (set.has('YEAR')) {
    ordered.push('YEAR');
  }
  return ordered.length ? ordered : ['MONTH'];
}

export default function ManagePlanDialog({
  open,
  onClose,
  organizationId,
  currentTier,
  currentBillingPeriod = 'MONTH',
  preferredBilling,
  canCompleteCheckout,
  embeddedInCheckout = false,
  checkoutCartPlanChange,
  checkoutCartLines,
  editingSubscriptionProductKey,
  /** When set, the tier column grid and billing options only use catalog rows for this product key (multi-SKU org plans). */
  subscriptionProductKeyFilter,
  checkoutQuotaAddons,
  onCartSaved,
  /** When opening manage for the parent so an add-on can be added: show warning subheader with required tier. */
  parentUpgradeRequiredForAddonMinTier,
}: {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  currentTier: SubscriptionTier;
  /** Billing cadence of the organization's current catalog plan (when set). */
  currentBillingPeriod?: SubscriptionBillingPeriod;
  /** When opening from “switch to annual”, pre-select this cadence when available. */
  preferredBilling?: SubscriptionBillingPeriod;
  /** Owner / procurement (or OSO-equivalent): pay immediately after saving the cart. Admins without this only save the draft cart. */
  canCompleteCheckout: boolean;
  embeddedInCheckout?: boolean;
  checkoutCartPlanChange?: SubscriptionCheckoutCartPlanChangeDraft | null;
  checkoutCartLines?: SubscriptionCheckoutCartLineDto[];
  editingSubscriptionProductKey?: string;
  subscriptionProductKeyFilter?: string;
  checkoutQuotaAddons?: { quotaKey: string; quantity: number }[];
  onCartSaved?: () => void;
  parentUpgradeRequiredForAddonMinTier?: SubscriptionTier;
}) {
  const {
    data: subscriptionPlans,
    isSuccess,
    isPending,
  } = useSubscriptionPlansQuery({
    entityType: 'Organization',
    enabled: open,
  });

  const { data: org, isPending: orgPending } = useGetOrganizationQuery(open ? organizationId : undefined);
  const { data: balances, isPending: balancesPending } = useQuotaBalances(
    open ? 'Organization' : undefined,
    open ? organizationId : undefined,
  );

  const patchCart = usePatchOrganizationSubscriptionCheckoutCart();
  const previewMut = usePreviewOrganizationPlanChangeMutation();
  const notification = useNotifications();
  const t = useTranslations();
  const isWideScreen = useBreakpointUp('sm');

  const cartFlowPending = patchCart.isPending;

  const normalizedOrgBilling = (currentBillingPeriod ?? 'MONTH') as SubscriptionBillingPeriod;

  const [userBilling, setUserBilling] = useState<SubscriptionBillingPeriod>(
    () => (preferredBilling ?? normalizedOrgBilling) as SubscriptionBillingPeriod,
  );

  /**
   * Per tier (not per billing period): extra units above each card's `included` baseline. Always derived
   * from {@link desiredTotalsByQuotaKey} so every card stays in sync — see {@link setLocalExtraForCard}.
   */
  const [quotaExtrasByCard, setQuotaExtrasByCard] = useState<Record<string, Record<string, number>>>(() =>
    normalizeQuotaExtrasByTierKeys(
      checkoutCartPlanChange ? quotaExtrasByCardFromPlanChangeDraft(checkoutCartPlanChange) : {},
    ),
  );
  const [changingTier, setChangingTier] = useState<SubscriptionTier | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmPreview, setConfirmPreview] = useState<OrganizationPlanChangePreview | null>(null);
  const [pendingCheckout, setPendingCheckout] = useState<ChangeOrganizationSubscriptionPlan | null>(null);
  const [pendingMeta, setPendingMeta] = useState<{ tierLabel: string; billingLabel: string } | null>(null);
  const [pendingPlan, setPendingPlan] = useState<SubscriptionPlan | null>(null);

  const catalogForProductLine = useMemo(() => {
    const all = subscriptionPlans?.plans ?? [];
    if (!subscriptionProductKeyFilter?.trim()) {
      return all;
    }
    const want = subscriptionProductKeyFilter.trim() || HOST_DEFAULT_PRODUCT_KEY;
    return all.filter((p) => (p.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) === want);
  }, [subscriptionPlans?.plans, subscriptionProductKeyFilter]);

  const productKeyForCopy = useMemo(
    () =>
      subscriptionProductKeyFilter?.trim() ||
      editingSubscriptionProductKey?.trim() ||
      catalogForProductLine[0]?.subscriptionProductKey?.trim() ||
      HOST_DEFAULT_PRODUCT_KEY,
    [subscriptionProductKeyFilter, editingSubscriptionProductKey, catalogForProductLine],
  );

  const managePlanProductDisplayName = useMemo(
    () =>
      resolveTranslatedProductField(t, 'subscription.organization.planPage.productName', productKeyForCopy) ??
      productKeyForCopy,
    [t, productKeyForCopy],
  );

  const managePlanProductClaim = useMemo(
    () => resolveProductClaimText(t, productKeyForCopy, currentTier),
    [t, productKeyForCopy, currentTier],
  );

  const addonParentUpgradeSubheader =
    parentUpgradeRequiredForAddonMinTier != null ? (
      <Stack spacing={1}>
        {managePlanProductClaim ? (
          <Typography variant="body2" color="text.secondary" component="div">
            {managePlanProductClaim}
          </Typography>
        ) : null}
        <Typography variant="body2" color="warning.main" component="div" sx={{ fontWeight: 500 }}>
          {t('subscription.organization.planPage.managePlanParentUpgradeForAddonSubtitle', {
            tier: t(
              `subscription.organization.tiers.${parentUpgradeRequiredForAddonMinTier}` as 'subscription.organization.tiers.FREE',
            ),
          })}
        </Typography>
      </Stack>
    ) : undefined;

  const addOnParentIsMonthly = useMemo(() => {
    if (!subscriptionProductKeyFilter?.trim() || !catalogForProductLine.length || !org) {
      return false;
    }
    const marker = catalogForProductLine.find((p) => p.requiresParent);
    if (!marker?.requiresParent) {
      return false;
    }
    const ppk = marker.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
    return resolveParentBillingPeriodForAddonFromOrgAndCart(org, checkoutCartLines, ppk) === 'MONTH';
  }, [subscriptionProductKeyFilter, catalogForProductLine, org, checkoutCartLines]);

  const billingOptions = useMemo(() => {
    const raw = buildBillingOptions(catalogForProductLine);
    if (addOnParentIsMonthly) {
      return raw.filter((b) => b === 'MONTH');
    }
    return raw;
  }, [catalogForProductLine, addOnParentIsMonthly]);

  const billing = billingOptions.includes(userBilling) ? userBilling : (billingOptions[0] ?? 'MONTH');

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!billingOptions.includes(userBilling)) {
      setUserBilling((billingOptions[0] ?? 'MONTH') as SubscriptionBillingPeriod);
    }
  }, [open, billingOptions, userBilling]);

  const subscriptionSnapshotForEditedProduct = editingSubscriptionProductKey
    ? org?.subscriptions?.find((s) => s.subscriptionProductKey === editingSubscriptionProductKey)
    : getPrimaryOrganizationSubscriptionInstance(org);

  const billingPeriodEndDisplayForQuotas = useMemo(
    () => formatSubscriptionInstancePeriodEndDisplay(subscriptionSnapshotForEditedProduct),
    [subscriptionSnapshotForEditedProduct],
  );

  const quotaCartAddonsDifferFromBooked = useMemo(() => {
    const pk = editingSubscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
    const line = checkoutCartLines?.find((l) => (l.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) === pk);
    const sub = subscriptionSnapshotForEditedProduct;
    if (!line || !sub) {
      return false;
    }
    let lineTier = line.tier?.trim() as SubscriptionTier | undefined;
    if (!lineTier && catalogForProductLine.length) {
      lineTier = soleTierFromPlansForProductKey(catalogForProductLine, pk);
    }
    if (lineTier !== currentTier) {
      return false;
    }
    if ((line.billingPeriod ?? 'MONTH') !== normalizedOrgBilling) {
      return false;
    }
    return !quotaAddonListsEqual(line.additionalQuotaUnits, sub.additionalQuotaUnits);
  }, [
    checkoutCartLines,
    catalogForProductLine,
    currentTier,
    editingSubscriptionProductKey,
    normalizedOrgBilling,
    subscriptionSnapshotForEditedProduct,
  ]);

  const columnPlans = !catalogForProductLine.length
    ? []
    : (ORDER.map((tier) => pickPlan(catalogForProductLine, tier, billing)).filter(Boolean) as SubscriptionPlan[]);

  const planCardKey = (p: SubscriptionPlan) => p.tier;

  const resolvePlanForUiByTier = (tier: SubscriptionTier) => {
    const raw = pickPlan(catalogForProductLine, tier, billing);
    if (!raw) {
      return undefined;
    }
    return normalizeSubscriptionPlanOverage(
      mergeOverageFromMatchingSubscriptionSnapshot(
        raw,
        subscriptionSnapshotForEditedProduct?.subscriptionPlan as SubscriptionPlan | undefined,
      ),
    );
  };

  /*
   * Seed the active tier card with the **booked** add-on quantities so its stepper renders the actual
   * subscription total (not just `included`). Then propagate the same per-quota desired total to every
   * other tier card (clamped to each card's `[included, max]`) so increases/decreases on the active card
   * keep all cards in sync. Runs once per `(open, currentTier, snapshot)` change and only seeds keys not
   * already present on the active card so user edits within the same session are preserved.
   */
  useEffect(() => {
    if (!open || !catalogForProductLine.length) {
      return;
    }
    const bookedUnits = subscriptionSnapshotForEditedProduct?.additionalQuotaUnits ?? [];
    if (!bookedUnits.length) {
      return;
    }
    const activePlan = resolvePlanForUiByTier(currentTier);
    if (!activePlan) {
      return;
    }
    setQuotaExtrasByCard((prev) => {
      const activeExisting = prev[currentTier] ?? {};
      const next: Record<string, Record<string, number>> = { ...prev };
      for (const u of bookedUnits) {
        const k = String(u.quotaKey ?? '').trim();
        if (!k || u.quantity <= 0) {
          continue;
        }
        if (activeExisting[k] !== undefined) {
          continue;
        }
        const includedActive = includedQuantityForKey(activePlan, k);
        const desiredTotal = includedActive + Math.max(0, u.quantity);
        for (const tier of ORDER) {
          const planForTier = resolvePlanForUiByTier(tier);
          if (!planForTier) {
            continue;
          }
          const includedT = includedQuantityForKey(planForTier, k);
          const capT = maxQuantityForKey(planForTier, k);
          const maxExtraT = capT !== undefined ? Math.max(0, capT - includedT) : Number.POSITIVE_INFINITY;
          const extraT = Math.min(Math.max(0, desiredTotal - includedT), maxExtraT);
          next[tier] = { ...(next[tier] ?? {}), [k]: extraT };
        }
      }
      return next;
    });
    // Re-run when the user opens the dialog, swaps subscription, or switches billing cadence
    // (which changes which catalog row backs `resolvePlanForUiByTier`).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, currentTier, subscriptionSnapshotForEditedProduct, billing, catalogForProductLine.length]);

  /** Persist cart only; Mollie is triggered from the plan page Checkout button (RBAC: configure vs pay). */
  const runCartFlow = async (checkoutPayload: ChangeOrganizationSubscriptionPlan, selectedPlan: SubscriptionPlan) => {
    const allPlans = subscriptionPlans?.plans ?? [];
    const mergeCheckout =
      embeddedInCheckout &&
      checkoutCartLines?.length &&
      editingSubscriptionProductKey &&
      checkoutCartLines.some((l) => l.subscriptionProductKey === editingSubscriptionProductKey);

    if (mergeCheckout) {
      const parentKey = editingSubscriptionProductKey!;
      const editedCartLine = checkoutCartLines!.find((l) => l.subscriptionProductKey === editingSubscriptionProductKey);
      const prevParentBp = (editedCartLine?.billingPeriod ?? 'MONTH') as SubscriptionBillingPeriod;
      const nextParentBp = checkoutPayload.billingPeriod as SubscriptionBillingPeriod;
      const shouldCascadeAddonsToYearly =
        !selectedPlan.requiresParent && prevParentBp === 'MONTH' && nextParentBp === 'YEAR';
      const shouldCascadeAddonsToMonthly =
        !selectedPlan.requiresParent && prevParentBp === 'YEAR' && nextParentBp === 'MONTH';

      const siblingLines = checkoutCartLines!
        .filter((l) => l.subscriptionProductKey !== editingSubscriptionProductKey)
        .map((l) => {
          let body = cartLineDtoToPatchBody(l, allPlans);
          const effTier =
            (l.tier?.trim() as SubscriptionTier | undefined) ??
            soleTierFromPlansForProductKey(allPlans, l.subscriptionProductKey);
          const matchPlan = allPlans.find(
            (p) =>
              (p.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) ===
                (l.subscriptionProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY) &&
              effTier !== undefined &&
              p.tier === effTier &&
              (p.billingPeriod ?? 'MONTH') === ((l.billingPeriod ?? 'MONTH') as SubscriptionBillingPeriod),
          );
          const parentPkNorm = parentKey.trim() || HOST_DEFAULT_PRODUCT_KEY;
          if (shouldCascadeAddonsToYearly && matchPlan?.requiresParent) {
            const ppk = matchPlan.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
            if (ppk === parentPkNorm) {
              body = { ...body, billingPeriod: 'YEAR' };
            }
          }
          if (shouldCascadeAddonsToMonthly && matchPlan?.requiresParent) {
            const ppk = matchPlan.parentProductKey?.trim() || HOST_DEFAULT_PRODUCT_KEY;
            if (ppk === parentPkNorm) {
              body = { ...body, billingPeriod: 'MONTH' };
            }
          }
          return body;
        });
      const updatedLine = {
        subscriptionProductKey: editingSubscriptionProductKey!,
        tier: checkoutPayload.tier,
        billingPeriod: checkoutPayload.billingPeriod,
        additionalQuotaUnits: checkoutPayload.additionalQuotaUnits,
        ...(editedCartLine?.parentSubscriptionInstanceId
          ? { parentSubscriptionInstanceId: editedCartLine.parentSubscriptionInstanceId }
          : {}),
      };
      await patchCart.mutateAsync({
        organizationId,
        body: {
          lines: [...siblingLines, updatedLine],
          quotaAddons: checkoutQuotaAddons ?? [],
        },
      });
    } else {
      const productKey = (
        editingSubscriptionProductKey?.trim() ||
        selectedPlan.subscriptionProductKey?.trim() ||
        HOST_DEFAULT_PRODUCT_KEY
      ).toString();
      const otherLines = (checkoutCartLines ?? [])
        .filter((l) => l.subscriptionProductKey !== productKey)
        .map((l) => cartLineDtoToPatchBody(l, allPlans));
      const existingLine = checkoutCartLines?.find((l) => l.subscriptionProductKey === productKey);
      const parentFromResolve = resolveAddOnParentInstanceId(org, selectedPlan, checkoutCartLines);
      const parentId =
        existingLine?.parentSubscriptionInstanceId ||
        (!('error' in parentFromResolve) && 'parentSubscriptionInstanceId' in parentFromResolve
          ? parentFromResolve.parentSubscriptionInstanceId
          : undefined);
      const updatedLine = {
        subscriptionProductKey: productKey,
        tier: checkoutPayload.tier,
        billingPeriod: checkoutPayload.billingPeriod,
        additionalQuotaUnits: checkoutPayload.additionalQuotaUnits,
        ...(parentId ? { parentSubscriptionInstanceId: parentId } : {}),
      };
      await patchCart.mutateAsync({
        organizationId,
        body: {
          lines: [...otherLines, updatedLine],
          quotaAddons: checkoutQuotaAddons ?? [],
        },
      });
    }
    if (embeddedInCheckout) {
      notification.notifySuccess(t('subscription.organization.planPage.cartUpdatedInCheckout'));
    } else if (onCartSaved) {
      notification.notifySuccess(t('subscription.organization.planPage.cartSavedOpeningCheckout'));
    } else if (canCompleteCheckout) {
      notification.notifySuccess(t('subscription.organization.planPage.cartSavedUseCheckoutBanner'));
    } else {
      notification.notifySuccess(t('subscription.organization.planPage.cartSavedToast'));
    }
    if (!embeddedInCheckout) {
      onCartSaved?.();
    }
    onClose();
  };

  /**
   * On any tier card change, treat `included(sourceTier) + nextExtra` as the **shared desired total** for
   * that quota key and re-derive extras for every other tier card by clamping that total to each card's
   * `[included, max]` range. This keeps cards in sync in both directions (increase **and** decrease) so
   * the user never sees stale numbers across tiers.
   */
  const setLocalExtraForCard = (sourceTier: SubscriptionTier) => (quotaKey: string, nextExtra: number) => {
    setQuotaExtrasByCard((prev) => {
      const sourcePlan = resolvePlanForUiByTier(sourceTier);
      if (!sourcePlan) {
        return { ...prev, [sourceTier]: { ...(prev[sourceTier] ?? {}), [quotaKey]: Math.max(0, nextExtra) } };
      }

      const includedSource = includedQuantityForKey(sourcePlan, quotaKey);
      const capSource = maxQuantityForKey(sourcePlan, quotaKey);
      const maxExtraSource =
        capSource !== undefined ? Math.max(0, capSource - includedSource) : Number.POSITIVE_INFINITY;
      const clampedExtraSource = Math.min(Math.max(0, nextExtra), maxExtraSource);
      const desiredTotal = includedSource + clampedExtraSource;

      const merged: Record<string, Record<string, number>> = { ...prev };
      for (const tier of ORDER) {
        const planForTier = resolvePlanForUiByTier(tier);
        if (!planForTier) {
          continue;
        }
        const includedT = includedQuantityForKey(planForTier, quotaKey);
        const capT = maxQuantityForKey(planForTier, quotaKey);
        const maxExtraT = capT !== undefined ? Math.max(0, capT - includedT) : Number.POSITIVE_INFINITY;
        /** Each card clamps the shared total to its own [included, max]; cards with `included >= desiredTotal` show extra=0. */
        const extraT = Math.min(Math.max(0, desiredTotal - includedT), maxExtraT);
        merged[tier] = { ...(merged[tier] ?? {}), [quotaKey]: extraT };
      }
      return merged;
    });
  };

  const buildPlanChangePreviewRequest = (
    params: {
      tier: SubscriptionTier;
      billingPeriod: SubscriptionBillingPeriod;
      additionalQuotaUnits: { quotaKey: string; quantity: number }[];
    },
    catalogPlan: SubscriptionPlan,
  ) => {
    const allPlansForPreview = subscriptionPlans?.plans ?? [];
    const companionLine = (l: SubscriptionCheckoutCartLineDto) => ({
      subscriptionProductKey: l.subscriptionProductKey,
      tier: (l.tier?.trim() ||
        soleTierFromPlansForProductKey(allPlansForPreview, l.subscriptionProductKey) ||
        'FREE') as string,
      billingPeriod: l.billingPeriod as SubscriptionBillingPeriod | undefined,
      additionalQuotaUnits: l.additionalQuotaUnits,
      ...(l.parentSubscriptionInstanceId ? { parentSubscriptionInstanceId: l.parentSubscriptionInstanceId } : {}),
    });
    const base = {
      organizationId,
      tier: params.tier,
      billingPeriod: params.billingPeriod,
      additionalQuotaUnits: params.additionalQuotaUnits,
    };
    if (embeddedInCheckout) {
      const key = editingSubscriptionProductKey?.trim();
      if (!key) {
        return base;
      }
      const editedLine = checkoutCartLines?.find((l) => l.subscriptionProductKey === key);
      const siblings = (checkoutCartLines ?? []).filter((l) => l.subscriptionProductKey !== key);
      return {
        ...base,
        subscriptionProductKey: key,
        ...(editedLine?.parentSubscriptionInstanceId
          ? { parentSubscriptionInstanceId: editedLine.parentSubscriptionInstanceId }
          : {}),
        ...(siblings.length ? { companionCartLines: siblings.map(companionLine) } : {}),
      };
    }
    const key = editingSubscriptionProductKey?.trim();
    if (!key) {
      return base;
    }
    const editedLine = checkoutCartLines?.find((l) => l.subscriptionProductKey === key);
    const siblings = (checkoutCartLines ?? []).filter((l) => l.subscriptionProductKey !== key);
    const parentRes = resolveAddOnParentInstanceId(org, catalogPlan, checkoutCartLines);
    const parentId =
      editedLine?.parentSubscriptionInstanceId ||
      (!('error' in parentRes) && 'parentSubscriptionInstanceId' in parentRes
        ? parentRes.parentSubscriptionInstanceId
        : undefined);
    return {
      ...base,
      subscriptionProductKey: key,
      ...(parentId ? { parentSubscriptionInstanceId: parentId } : {}),
      ...(siblings.length ? { companionCartLines: siblings.map(companionLine) } : {}),
    };
  };

  const handlePlanCheckout = async (plan: SubscriptionPlan, cardKey: string) => {
    /*
     * Local stepper edits in `quotaExtrasByCard[cardKey]` are the source of truth — they're seeded from
     * the booked subscription on open and mutated by the +/- buttons. The previous logic preferred a
     * stale cart line for the same booked row, which silently dropped the user's pending edits when
     * submitting "Update plan quotas".
     */
    const additionalQuotaUnits = Object.entries(quotaExtrasByCard[cardKey] ?? {})
      .filter(([, q]) => q > 0)
      .map(([quotaKey, quantity]) => ({ quotaKey, quantity }));
    const checkoutPayload: ChangeOrganizationSubscriptionPlan = {
      tier: plan.tier,
      organizationId,
      billingPeriod: plan.billingPeriod ?? 'MONTH',
      additionalQuotaUnits,
      redirectUrlAfterPayment: window.location.href,
    };

    setChangingTier(plan.tier);
    try {
      const preview = await previewMut.mutateAsync(
        buildPlanChangePreviewRequest(
          {
            tier: plan.tier,
            billingPeriod: plan.billingPeriod ?? 'MONTH',
            additionalQuotaUnits,
          },
          plan,
        ),
      );
      if (!preview.requiresConfirmationModal) {
        await runCartFlow(checkoutPayload, plan);
        return;
      }
      setConfirmPreview(preview);
      setPendingCheckout(checkoutPayload);
      setPendingPlan(plan);
      setPendingMeta({
        tierLabel: t(`subscription.organization.tiers.${plan.tier}`),
        billingLabel:
          billing === 'YEAR'
            ? t('subscription.organization.planPage.billedAnnually')
            : t('subscription.organization.planPage.billedMonthly'),
      });
      setConfirmOpen(true);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setChangingTier(null);
    }
  };

  if (!open) {
    return null;
  }

  const planModalShellProps = {
    size: (isWideScreen ? 'lg' : 'sm') as 'lg' | 'sm',
    width: isWideScreen ? 1000 : undefined,
    minHeight: isWideScreen ? 360 : undefined,
    maxHeight: isWideScreen ? ('min(92vh, calc(100vh - 48px))' as const) : undefined,
    sx: isWideScreen ? ({ overflow: 'hidden' } as const) : undefined,
    fullScreen: !isWideScreen,
    contentSx: isWideScreen
      ? ({ minHeight: 0, overflowY: 'auto' as const, flex: 1 } as const)
      : ({ overflow: 'auto' as const, minHeight: 0 } as const),
  };

  if (isPending || !subscriptionPlans || !isSuccess) {
    return (
      <Modal
        open
        handleClose={onClose}
        title={t('subscription.organization.planPage.choosePlan')}
        description={t('subscription.organization.planPage.choosePlanSubtitle')}
        {...planModalShellProps}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Modal>
    );
  }

  if (!columnPlans.length) {
    return (
      <Modal
        open
        handleClose={onClose}
        title={t('subscription.organization.planPage.choosePlan')}
        description={t('subscription.organization.planPage.choosePlanSubtitle')}
        {...planModalShellProps}
      >
        <Typography color="text.secondary">{t('subscription.organization.planPage.noPlansInCatalog')}</Typography>
      </Modal>
    );
  }

  return (
    <Fragment>
      <Modal
        open={open}
        handleClose={onClose}
        title={t('subscription.organization.planPage.managePlanDialogTitle', {
          productName: managePlanProductDisplayName,
        })}
        description={addonParentUpgradeSubheader ? undefined : managePlanProductClaim}
        subtitle={addonParentUpgradeSubheader}
        {...planModalShellProps}
      >
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {columnPlans.map((plan, planIndex) => {
            const planForUi = normalizeSubscriptionPlanOverage(
              mergeOverageFromMatchingSubscriptionSnapshot(
                plan,
                subscriptionSnapshotForEditedProduct?.subscriptionPlan as SubscriptionPlan | undefined,
              ),
            );
            const previousPlan = planIndex > 0 ? columnPlans[planIndex - 1] : undefined;
            const previousPlanForUi = previousPlan ? normalizeSubscriptionPlanOverage(previousPlan) : undefined;
            const cardKey = planCardKey(plan);
            const isCurrent = plan.tier === currentTier && billing === normalizedOrgBilling;
            /** Booked tier column (billing toggle may differ — border still marks “your” tier). */
            const isBookedTierColumn = plan.tier === currentTier;
            const sameTierOtherBilling = plan.tier === currentTier && billing !== normalizedOrgBilling;
            const cardExtras = quotaExtrasByCard[cardKey] ?? {};
            const cardExtrasSeeded = Object.keys(cardExtras).length > 0;
            /*
             * Compare the dialog's local quota edits for the active card to the booked subscription's
             * `additionalQuotaUnits` so the primary button can switch to "Update plan quotas" the moment
             * the user touches a stepper — independent of whether the change has been pushed to the cart
             * line yet. (`quotaCartAddonsDifferFromBooked` only reflects the cart vs. booked diff.)
             */
            const cardExtrasAsAddonList = Object.entries(cardExtras)
              .filter(([, q]) => q > 0)
              .map(([quotaKey, quantity]) => ({ quotaKey, quantity }));
            const localExtrasDifferFromBooked =
              cardExtrasSeeded &&
              !quotaAddonListsEqual(cardExtrasAsAddonList, subscriptionSnapshotForEditedProduct?.additionalQuotaUnits);
            const activeCardHasPendingEdits = isCurrent && localExtrasDifferFromBooked;
            /*
             * Use `computePreviewSubscriptionPrice` once the per-card extras have been seeded so the
             * displayed price reflects local stepper edits (including the active card). Fall back to the
             * booked renewal only while we're still loading the subscription snapshot — this prevents
             * the active card from showing €80.00 indefinitely when seats are bumped via the stepper.
             */
            const displayPrice = cardExtrasSeeded
              ? computePreviewSubscriptionPrice(planForUi, cardExtras)
              : isCurrent
                ? computeOrganizationRenewalAmount(
                    planForUi,
                    subscriptionSnapshotForEditedProduct?.additionalQuotaUnits,
                  )
                : computePreviewSubscriptionPrice(planForUi, cardExtras);
            const tierLabel = t(`subscription.organization.tiers.${plan.tier}`);
            const isYearBilling = billing === 'YEAR';
            const recurringFull = displayPrice;
            const recurringDisplayEur = isYearBilling ? Math.round((recurringFull / 12) * 100) / 100 : recurringFull;
            const periodShortForCard =
              (plan.billingPeriod ?? 'MONTH') === 'YEAR'
                ? t('subscription.organization.planPage.billingYearShort')
                : t('subscription.organization.planPage.billingPeriodShort');
            const annualPriceSuffixFull = t(
              'subscription.organization.planPage.subscriptionCheckoutRecurringPerMoAnnualSuffix',
            );
            const annualPriceSuffixCommaIdx = annualPriceSuffixFull.indexOf(',');
            const annualPriceSuffixHead =
              annualPriceSuffixCommaIdx >= 0
                ? annualPriceSuffixFull.slice(0, annualPriceSuffixCommaIdx)
                : annualPriceSuffixFull;
            const annualPriceSuffixTail =
              annualPriceSuffixCommaIdx >= 0 ? annualPriceSuffixFull.slice(annualPriceSuffixCommaIdx) : '';
            const showPriceBillingSuffix = recurringDisplayEur > 0;
            const priceSuffixTail = isYearBilling
              ? annualPriceSuffixTail
              : `, ${t('event.checkoutModal.excludingVat')}`;
            const prevTier = previousPlan?.tier ?? plan.tier;
            const marketingBullets = getManagePlanFeatureListBullets(t, productKeyForCopy, plan.tier);
            const includedQuotaRows = resolveIncludedQuotaDisplayRows(
              t,
              planForUi.includedQuotas,
              previousPlanForUi?.includedQuotas,
            );
            const includedFeatureRows = resolveIncludedFeatureKeyDisplayRows(
              t,
              plan.featureKeys,
              previousPlan?.featureKeys,
            );
            const showIncludesBody =
              marketingBullets.length > 0 || includedQuotaRows.length > 0 || includedFeatureRows.length > 0;
            /** FREE row: only offer “Cancel plan” (downgrade to free) when a paid tier is actually booked. */
            const freeCancelToFreeTier = plan.tier === 'FREE' && !isCurrent && currentTier !== 'FREE';
            const freeTierPresentsAsCurrent = plan.tier === 'FREE' && !freeCancelToFreeTier;

            return (
              <Grid size={tierCardGridSize(columnPlans.length)} key={`${plan.tier}-${plan.billingPeriod ?? 'MONTH'}`}>
                <Card
                  variant="outlined"
                  sx={(theme) => ({
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    borderStyle: 'solid',
                    borderWidth: isBookedTierColumn ? 2 : 1,
                    borderColor: isBookedTierColumn ? alpha(theme.palette.primary.main, 0.38) : theme.palette.divider,
                  })}
                >
                  <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="subtitle1" fontWeight={600} noWrap sx={{ minWidth: 0 }}>
                          {tierLabel}
                        </Typography>
                      </Stack>
                      {billingOptions.length > 1 && plan.tier !== 'FREE' ? (
                        <Box sx={{ flexShrink: 0 }}>
                          <ToggleButtonGroup
                            size="small"
                            value={billing}
                            exclusive
                            onChange={(_, v) => {
                              if (v !== null) {
                                setUserBilling(v);
                              }
                            }}
                            aria-label={t('subscription.organization.planPage.billingCadence')}
                            sx={{
                              '& .MuiToggleButton-root': {
                                fontSize: '0.8125rem',
                                lineHeight: 1.2,
                                py: 0.5,
                                px: 1,
                                minHeight: 28,
                              },
                            }}
                          >
                            <ToggleButton value="MONTH" size="small">
                              {t('subscription.organization.planPage.billingMonthly')}
                            </ToggleButton>
                            <ToggleButton value="YEAR" size="small">
                              {t('subscription.organization.planPage.billingAnnual')}
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Box>
                      ) : null}
                    </Stack>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 0.25 }}>
                      <Typography variant="h5" component="span" fontWeight={600}>
                        €{recurringDisplayEur.toFixed(2)}
                      </Typography>
                      {showPriceBillingSuffix ? (
                        <>
                          <Typography component="span" variant="body2" color="text.secondary">
                            {isYearBilling ? annualPriceSuffixHead : ` /${periodShortForCard}`}
                          </Typography>
                          {priceSuffixTail ? (
                            <Typography
                              component="span"
                              variant="caption"
                              color="text.secondary"
                              sx={{ lineHeight: 1.4 }}
                            >
                              {priceSuffixTail}
                            </Typography>
                          ) : null}
                        </>
                      ) : null}
                    </Box>
                    <PlanCardQuotaSteppers
                      compact
                      plan={planForUi}
                      organizationId={organizationId}
                      subscription={subscriptionSnapshotForEditedProduct}
                      balances={balances}
                      balancesLoading={orgPending || balancesPending}
                      isActivePlan={isCurrent}
                      localQuotaExtra={quotaExtrasByCard[cardKey] ?? {}}
                      onSetLocalExtra={setLocalExtraForCard(cardKey)}
                      billingPeriodEndDisplay={billingPeriodEndDisplayForQuotas}
                    />
                    <Divider />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                      {planIndex === 0
                        ? t('subscription.organization.planPage.includesLabel')
                        : t('subscription.organization.planPage.everythingInPrevious', {
                            previous: t(`subscription.organization.tiers.${prevTier}`),
                          })}
                    </Typography>
                    {showIncludesBody ? (
                      <ManagePlanIncludesList
                        productKey={productKeyForCopy}
                        tier={plan.tier}
                        includedQuotas={planForUi.includedQuotas}
                        featureKeys={plan.featureKeys}
                        previousIncludedQuotas={previousPlanForUi?.includedQuotas}
                        previousFeatureKeys={previousPlan?.featureKeys}
                      />
                    ) : null}
                    <Button
                      type="button"
                      fullWidth
                      variant={isCurrent || freeTierPresentsAsCurrent ? 'outlined' : 'contained'}
                      size="medium"
                      disabled={
                        (plan.tier === 'FREE'
                          ? !freeCancelToFreeTier
                          : isCurrent && !quotaCartAddonsDifferFromBooked && !activeCardHasPendingEdits) ||
                        cartFlowPending ||
                        previewMut.isPending
                      }
                      loading={
                        (cartFlowPending && changingTier === plan.tier) ||
                        (previewMut.isPending && changingTier === plan.tier)
                      }
                      onClick={() => void handlePlanCheckout(plan, cardKey)}
                    >
                      {isCurrent
                        ? quotaCartAddonsDifferFromBooked || activeCardHasPendingEdits
                          ? t('subscription.organization.planPage.updatePlanQuotas')
                          : t('subscription.organization.planPage.currentPlan')
                        : plan.tier === 'FREE'
                          ? t(
                              freeCancelToFreeTier
                                ? 'subscription.organization.planPage.cancelPlan'
                                : 'subscription.organization.planPage.currentPlan',
                            )
                          : sameTierOtherBilling
                            ? t('subscription.organization.planPage.switchBilling')
                            : t('subscription.organization.planPage.upgradeOrSwitch')}
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <WorkspacePremiumIcon color="action" />
              <Stack spacing={0.25}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {t('subscription.organization.planPage.enterpriseTitle')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('subscription.organization.planPage.enterpriseDescription')}
                </Typography>
              </Stack>
            </Stack>
            <Button variant="outlined" href="mailto:info@britestage.com" component="a">
              {t('subscription.organization.planPage.contactUs')}
            </Button>
          </Stack>
        </Paper>
      </Modal>
      <SubscriptionPlanChangeConfirmModal
        open={confirmOpen}
        onClose={() => {
          if (!cartFlowPending) {
            setConfirmOpen(false);
            setConfirmPreview(null);
            setPendingCheckout(null);
            setPendingMeta(null);
            setPendingPlan(null);
          }
        }}
        preview={confirmPreview}
        targetTierLabel={pendingMeta?.tierLabel ?? ''}
        billingLabel={pendingMeta?.billingLabel ?? ''}
        onConfirm={() => {
          if (!pendingCheckout || !pendingPlan) {
            return;
          }
          void (async () => {
            try {
              await runCartFlow(pendingCheckout, pendingPlan);
              setConfirmOpen(false);
              setConfirmPreview(null);
              setPendingCheckout(null);
              setPendingMeta(null);
              setPendingPlan(null);
            } catch (e) {
              window.alert(e instanceof Error ? e.message : 'Request failed');
            }
          })();
        }}
        confirmLoading={cartFlowPending}
      />
    </Fragment>
  );
}
